class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 120);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      const target = event.target;

      // WATCHERS SPEED FIX:
      // checkbox/select facets submit immediately; only typed/range inputs debounce briefly.
      if (target.matches('input[type="checkbox"], select')) {
        this.onSubmitHandler(event);
        return;
      }

      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      // WATCHERS: facets.js can stay loaded while persistent navigation moves
      // back to Home or another non-collection page. Do nothing when there is
      // no live collection grid to render.
      if (!document.getElementById('product-grid') || !document.getElementById('ProductGridContainer')) return;

      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static resetForNavigation(searchParams = '') {
    // Persistent navigation changes collection context without reloading this
    // JavaScript file. Never carry facet response cache/history state from the
    // previous page into the newly activated collection.
    FacetFiltersForm.filterData = [];
    FacetFiltersForm.watchersQuicklinkPreloads.clear();
    FacetFiltersForm.searchParamsInitial = searchParams;
    FacetFiltersForm.searchParamsPrev = searchParams;
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else if (FacetFiltersForm.watchersQuicklinkPreloads.has(url)) {
        FacetFiltersForm.renderSectionFromPreload(url, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

        FacetFiltersForm.storeFilterData(url, html);
        FacetFiltersForm.renderFilters(parsedHTML, event);
        FacetFiltersForm.renderProductGridContainer(parsedHTML);
        FacetFiltersForm.renderProductCount(parsedHTML);
        FacetFiltersForm.renderWatchersQuicklinks(parsedHTML);
        FacetFiltersForm.preloadWatchersQuicklinks(document);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger();
        }
      });
  }

  static renderSectionFromPreload(url, event) {
    const preload = FacetFiltersForm.watchersQuicklinkPreloads.get(url);

    if (!preload) {
      FacetFiltersForm.renderSectionFromFetch(url, event);
      return;
    }

    preload.then((html) => {
      if (!html) {
        FacetFiltersForm.renderSectionFromFetch(url, event);
        return;
      }

      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

      FacetFiltersForm.storeFilterData(url, html);
      FacetFiltersForm.renderFilters(parsedHTML, event);
      FacetFiltersForm.renderProductGridContainer(parsedHTML);
      FacetFiltersForm.renderProductCount(parsedHTML);
      FacetFiltersForm.renderWatchersQuicklinks(parsedHTML);
      FacetFiltersForm.preloadWatchersQuicklinks(document);

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }
    });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.renderWatchersQuicklinks(parsedHTML);
    FacetFiltersForm.preloadWatchersQuicklinks(document);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
  }

  static storeFilterData(url, html) {
    const existingIndex = FacetFiltersForm.filterData.findIndex((entry) => entry.url === url);

    if (existingIndex >= 0) {
      FacetFiltersForm.filterData[existingIndex] = { html, url };
    } else {
      FacetFiltersForm.filterData.push({ html, url });
    }

    // Keep the shared Dawn cache bounded while still retaining the active
    // quicklink states that make the 2-6 visible shortcuts feel immediate.
    if (FacetFiltersForm.filterData.length > 40) {
      FacetFiltersForm.filterData.splice(0, FacetFiltersForm.filterData.length - 40);
    }
  }

  static getWatchersQuicklinkSectionUrl(anchor) {
    if (!anchor?.href || !window.location.pathname.startsWith('/collections/')) return null;

    let targetUrl;

    try {
      targetUrl = new URL(anchor.href, window.location.href);
    } catch (_) {
      return null;
    }

    if (targetUrl.origin !== window.location.origin) return null;
    if (targetUrl.pathname !== window.location.pathname) return null;

    const section = FacetFiltersForm.getSections()[0];
    if (!section?.section) return null;

    const searchParams = targetUrl.search.slice(1);
    return `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
  }

  static preloadWatchersQuicklinks(root = document) {
    if (!window.location.pathname.startsWith('/collections/')) return;
    if (!root?.querySelectorAll) return;

    const anchors = Array.from(
      root.querySelectorAll('.watchers-collection-quick-filter[href]')
    );

    const urls = new Set();

    anchors.forEach((anchor) => {
      const url = FacetFiltersForm.getWatchersQuicklinkSectionUrl(anchor);
      if (url) urls.add(url);
    });

    urls.forEach((url) => {
      if (FacetFiltersForm.filterData.some((entry) => entry.url === url)) return;
      if (FacetFiltersForm.watchersQuicklinkPreloads.has(url)) return;

      const preload = fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'WATCHERS-Facet-Quicklink-Preload'
        }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Quicklink preload failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          FacetFiltersForm.storeFilterData(url, html);
          return html;
        })
        .catch(() => null)
        .finally(() => {
          FacetFiltersForm.watchersQuicklinkPreloads.delete(url);
        });

      FacetFiltersForm.watchersQuicklinkPreloads.set(url, preload);
    });
  }

  static renderWatchersQuicklinks(parsedHTML) {
    const source = parsedHTML.querySelector('[data-watchers-smart-quicklinks]');
    const current = document.querySelector('[data-watchers-smart-quicklinks]');

    if (!source || !current) return;

    current.replaceWith(document.importNode(source, true));
  }

  static renderProductGridContainer(parsedHTML) {
    document.getElementById('ProductGridContainer').innerHTML = parsedHTML
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(parsedHTML) {
    const count = parsedHTML.getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.watchersQuicklinkPreloads = new Map();
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

const warmWatchersFacetQuicklinks = () => {
  window.requestAnimationFrame(() => {
    FacetFiltersForm.preloadWatchersQuicklinks(document);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', warmWatchersFacetQuicklinks, { once: true });
} else {
  warmWatchersFacetQuicklinks();
}

document.addEventListener('watchers:navigation:complete', warmWatchersFacetQuicklinks);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
